import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { translate } from '@jsverse/transloco';

import { WidgetCommentsHandlerService } from 'app/modules/widget/services/widget-comments-handler.service';

import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import { AuthService } from '../../../../../../providers/bridge-api/auth.service';
import { Comment } from '../../../../../../providers/bridge-api/dashboard.service';
import { SemantriaUsersService } from '../../../../../../providers/semantria-api/semantria-users.service';
import { Utils } from '../../../../../../utils/utils';
import { CommentDataChange } from '../../../../services/chart-report/chart-report-abstract/chart-report-abstract';

@EnableClassTelemetry()
@Component({
  selector: 'app-comment-card',
  templateUrl: './comment-card.component.html',
  styleUrls: ['./comment-card.component.scss'],
  standalone: false,
})
export class CommentCardComponent implements OnChanges {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();

  @ViewChild('cardElement') cardElement: ElementRef<HTMLDivElement>;

  @Input() set comment(value: Comment) {
    this._comment = value;
    this.userService.getDisplayNameForUser(this._comment.createdBy).then((user) => {
      this.createdBy = user;
    });
  }
  @Input() isCommentVisible: boolean;
  @Input() isSelected: boolean;
  @Input() isPinned: boolean;
  @Input() smartSummaryExists: boolean;
  @Input() commentChange: CommentDataChange;
  @Input() isDashboardLocked: boolean;

  @Output() openSmartSummary = new EventEmitter<string>();
  @Output() delete = new EventEmitter<Comment>();
  @Output() open = new EventEmitter<Comment>();

  private userService: SemantriaUsersService = inject(SemantriaUsersService);
  authService: AuthService = inject(AuthService);
  commentsHandlerService: WidgetCommentsHandlerService = inject(WidgetCommentsHandlerService);

  private _comment: Comment;
  createdBy: string;
  indicators = [
    {
      name: 'data-changed',
      icon: 'document_alert_filled',
      color: 'error',
      visible: false,
      disabled: false,
      tooltipText: translate('comment-card.indicators.data-changed'),
    },
    {
      name: 'pinned',
      icon: 'pin_filled',
      color: 'orca',
      visible: false,
      disabled: false,
      tooltipText: translate('comment-card.indicators.comment-pinned'),
    },
    {
      name: 'smart-summary',
      icon: 'lightbulb',
      color: 'CX',
      visible: false,
      disabled: false,
      tooltipText: translate('comment-card.indicators.smart-summary'),
      clickHandler: this.openSmartSummaryClicked.bind(this),
    },
    {
      name: 'hidden',
      icon: 'visibility_off',
      color: 'orca',
      visible: false,
      disabled: false,
      tooltipText: translate('comment-card.indicators.comment-hidden'),
    },
  ];

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.smartSummaryExists &&
      this.comment.type === 'smart-summary' &&
      this.comment.smartSummaryId
    ) {
      const indicator = this.indicators.find((ind) => ind.name === this.comment.type);
      indicator.visible = this.smartSummaryExists;
      indicator.disabled = this.authService.isReadOnly || !this.smartSummaryExists;
      if (indicator.disabled) {
        indicator.tooltipText = translate('comment-card.indicators.disabled-smart-summary');
        if (!this.smartSummaryExists) {
          indicator.tooltipText = translate('comment-card.indicators.deleted-smart-summary');
        }
      }
    }

    if (changes.commentChange) {
      const commentChangeText = this.assembleDataChangeText();

      const indicator = this.indicators.find((ind) => ind.name === 'data-changed');
      indicator.visible = !!commentChangeText;
      indicator.tooltipText = commentChangeText;
    }

    if (changes.isCommentVisible) {
      const indicator = this.indicators.find((ind) => ind.name === 'hidden');
      indicator.visible = !this.isCommentVisible;
      indicator.tooltipText = translate('comment-card.indicators.comment-hidden');
    }

    if (changes.isPinned) {
      const indicator = this.indicators.find((ind) => ind.name === 'pinned');
      indicator.visible = this.isPinned;
      indicator.tooltipText = translate('comment-card.indicators.comment-pinned');
    }
  }

  private openSmartSummaryClicked(): void {
    this.openSmartSummary.emit(this.comment.smartSummaryId);
  }

  private assembleDataChangeText() {
    if (!this.comment || !this.commentChange) {
      return;
    }

    const changedProp = this.commentChange.y !== undefined ? 'y' : 'x';

    let dataChangeText = translate('widgets.comments.list.change-reason.data-changed-from', {
      oldValue: Utils.round(this.comment.target.point[changedProp], 2),
    });
    if (!isNaN(this.commentChange[changedProp])) {
      dataChangeText += translate('widgets.comments.list.change-reason.data-changed-to', {
        newValue: Utils.round(this.commentChange[changedProp], 2),
      });
    }
    if (this.commentChange.reasons.length === 1 && this.commentChange.reasons[0] !== 'UNKNOWN') {
      dataChangeText += translate(
        `widgets.comments.list.change-reason.due-to.${this.commentChange.reasons[0]}`
      );
    }

    return dataChangeText;
  }

  deleteComment(): void {
    this.delete.emit(this.comment);
  }

  openComment(): void {
    this.open.emit(this.comment);
  }

  get comment() {
    return this._comment;
  }
}
